import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import useInitPurchase from '@hooks/useInitPurchase';
import { wrapper } from 'store';
import FormLayout from '@/containers/formLayout';
import Address from '@/containers/address';
import PersonalData from '@/containers/personalData';
import FormMobileLineFake from '@/components/fakes/formPagesFake/formMobileLineFake';
import ChooseOperation from '@/containers/chooseOperationArr';
import Shipping from '@/containers/shipping';
import Payment from '@/containers/payment';
import { SEGMENT, BUBBLE_TEXTS } from '@/constants/constant';
import {
  URL_SEO,
  PATH_NAME,
  FORM_ID,
  TRANSACTION_TYPE,
} from '@/constants/pages/mobileLines';
import { OPERATION } from '@/constants/operation';
import { getStaticProps as getStaticPropsMethod } from '@/bit/pages/pages.create-pages';
import getDeviceKindDependsOnViewPort from '@gcloud-npm/utils.common.get-device-kind-depends-on-viewport';
import { actions as formActions } from '@/reducers/form-mobile-line-reducer';
import { actions as addressActions } from '@/reducers/address-reducer';
import { BILLING_ADDRESS } from '@/constants/billingAddress';
import { calculateShippingText } from '@/utils/orionTextHelper/lm';
import { sendTaggingInfo } from '@/utils/common';

const MobileLineNoClientForm = ({ segment = SEGMENT.particulares }) => {
  const dispatch = useDispatch();
  const formMobileLine = useSelector((state) => state.formMobileLine);
  const { shoppingCart, loadingShoppingCart, checkout, lines, orionTexts } =
    formMobileLine;
  const [funnelTaggingInfoSent, setFunnelTaggingInfoSent] = useState(false);
  const [showErrorBubble, setShowErrorBubble] = useState(false);
  const [calculatedShippingTexts, setCalculatedShippingTexts] = useState({});
  const defaultValues = { [OPERATION.ID]: [] };
  const {
    unregister,
    getValues,
    handleSubmit,
    resetField,
    control,
    watch,
    setValue,
  } = useForm({
    mode: 'onTouched',
    defaultValues,
  });
  const operationFormData = useWatch({ control, name: OPERATION.ID });
  const { setSegment, initPurchase } = formActions;
  const { setZone } = addressActions;
  const [formLoaded, setFormLoaded] = useState(false);

  useInitPurchase({
    segment,
    initPurchase,
    setSegment,
    setZone,
  });

  const onSubmit = async (formData) => {
    setShowErrorBubble(false);
    const deviceKind = getDeviceKindDependsOnViewPort();
    dispatch(
      formActions.checkout({
        formData,
        deviceKind,
      })
    );
  };
  const onError = (formErrors) => {
    if (Object.keys(formErrors).length > 1) {
      setShowErrorBubble(true);
      const scrollElement = document.getElementById(FORM_ID.GP);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    } else if (Object.keys(formErrors).length === 1) {
      setShowErrorBubble(false);
      const scrollElement = document.getElementById(Object.keys(formErrors)[0]);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    }
  };

  useEffect(() => {
    if (Array.isArray(operationFormData)) {
      setCalculatedShippingTexts(
        calculateShippingText({ orionTexts, operationFormData })
      );
    }
  }, [operationFormData]);

  useEffect(() => {
    if (
      lines &&
      (!Array.isArray(operationFormData) ||
        (Array.isArray(operationFormData) && operationFormData.length === 0))
    ) {
      setValue(OPERATION.ID, Array.isArray(lines) ? lines : []);
      setFormLoaded(true);
    }
  }, [lines]);

  useEffect(() => {
    if (
      shoppingCart &&
      !funnelTaggingInfoSent &&
      window.grecaptcha &&
      window.grecaptcha.enterprise
    ) {
      window.grecaptcha.enterprise.ready(() => {
        const { telcoProductName, totalRecurringPrice } = {
          ...shoppingCart,
        };
        const { finalPrice } = {
          ...totalRecurringPrice,
        };
        sendTaggingInfo(
          {
            product: telcoProductName,
            price: finalPrice,
            funnelPurchaseMode: '',
            userType: '',
            funnelStepName: 'formulario',
            transactionType: TRANSACTION_TYPE,
            funnelFlowName: 'movistar-layer',
          },
          '',
          'movistar_layer_google_ads'
        );
        setFunnelTaggingInfoSent(true);
      });
    }
  }, [shoppingCart]);

  return (
    <>
      <Head>
        <script
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_LAYER_RECAPTCHA_ID}`}
          async
        />
      </Head>
      <FormLayout
        pageId={FORM_ID.GP}
        onSubmit={handleSubmit(onSubmit, onError)}
        control={control}
        showErrorBubble={showErrorBubble}
        textBubble={BUBBLE_TEXTS.BUBBLE_ERROR_TEXT}
        loaded={formLoaded}
        shoppingCart={shoppingCart}
        loadingShoppingCart={loadingShoppingCart}
        loadingButtonShoppingCart={checkout}
        formFake={<FormMobileLineFake />}
        urlLoginCardRedirect={shoppingCart ? document.location.href : ''}
      >
        <PersonalData control={control} />
        <Address
          loaded={!!shoppingCart}
          unregister={unregister}
          control={control}
          getValues={getValues}
          resetField={resetField}
          componentName={BILLING_ADDRESS.ID}
          formStructure={BILLING_ADDRESS}
          setInfo={formActions.setInfo}
          setValue={setValue}
        />
        <ChooseOperation
          title={Array.isArray(lines) && lines.length > 1 ? '' : 'Línea móvil'}
          control={control}
          getValues={getValues}
          resetField={resetField}
          unregister={unregister}
          lines={lines}
          watch={watch}
          setValue={setValue}
        />
        <Shipping
          control={control}
          getValues={getValues}
          unregister={unregister}
          resetField={resetField}
          shippingTitle={calculatedShippingTexts?.title}
          shippingText={calculatedShippingTexts?.description}
          setValue={setValue}
        />
        <Payment loaded={!!shoppingCart} control={control} />
      </FormLayout>
    </>
  );
};
export default MobileLineNoClientForm;

export const getStaticProps = wrapper.getStaticProps(async (data) => {
  return getStaticPropsMethod(data, '', 2, {
    pathname: PATH_NAME.GP,
    orionAlias: '',
    seoLocation: URL_SEO,
    headerLocation: '/',
    layoutInfo: '',
  });
});
